/* Views for the 4 new MET & Multi-trait analyses (Phase 1).
   All views are pure-render, consuming results JSON from backend.
   They also render cross-analysis "Run X" buttons that stash CSV in sessionStorage.
*/
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2, ArrowRight, ChevronRight } from 'lucide-react';
import IndiaMegaEnvMap from './IndiaMegaEnvMap';

/* ============================================================
   Cross-analysis transfer helper
============================================================ */
function TransferButtons({ analysisId, targetKeys, label = 'Next step' }) {
  const navigate = useNavigate();
  if (!targetKeys || !targetKeys.length) return null;

  const transfer = async (targetKey, sectionId) => {
    // Fetch the original file (not saved — we only have the results). Instead,
    // stash the analysisId reference so the target page can fetch cell means.
    // Simpler: ask the user to upload the same CSV — but we can pre-populate via
    // server-side "source dataset" endpoint if needed. For now, just navigate
    // with a hint: the target's upload card will show a toast.
    sessionStorage.setItem(`bk_transfer_hint_${targetKey}`, JSON.stringify({ from: analysisId }));
    navigate(`/biometrical-analysis?section=${sectionId}&design=${targetKey}`);
  };

  return (
    <div className="mt-6 rounded-xl p-4" style={{ background: 'linear-gradient(135deg, #F0FDF4, #ECFDF5)', border: '1px solid #86EFAC' }} data-testid="transfer-buttons">
      <div className="flex items-center gap-2 mb-3">
        <Link2 size={14} style={{ color: '#059669' }} />
        <span className="font-body" style={{ fontSize: 12, fontWeight: 700, color: '#064E3B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {label}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {targetKeys.map(t => (
          <button
            key={t.key}
            onClick={() => transfer(t.key, t.section || 'stability')}
            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all btn-press"
            style={{
              fontSize: 12, fontWeight: 600,
              background: 'white', color: '#059669', border: '1.5px solid #059669',
            }}
            data-testid={`transfer-${t.key}`}
          >
            {t.label} <ArrowRight size={12} />
          </button>
        ))}
      </div>
    </div>
  );
}

/* Shared pretty-number */
const fmt = (v, n = 4) => {
  if (v === null || v === undefined || v === '-') return '-';
  if (typeof v === 'number') {
    if (Math.abs(v) < 0.0001 && v !== 0) return v.toExponential(2);
    return v.toFixed(n);
  }
  return String(v);
};

function AnovaTable({ rows, testIdSuffix }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="overflow-x-auto" data-testid={`anova-${testIdSuffix}`}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-[#2D6A4F] text-white">
            {['Source', 'DF', 'SS', 'MS', 'F', 'p', 'Sig'].map(h => (
              <th key={h} className="px-3 py-2 text-center text-xs font-semibold border border-[#1B4332]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
              <td className="px-3 py-1.5 border border-[#E8E6E1] text-xs">{r.Source}</td>
              <td className="px-3 py-1.5 border border-[#E8E6E1] text-xs text-center font-mono">{r.DF}</td>
              <td className="px-3 py-1.5 border border-[#E8E6E1] text-xs text-center font-mono">{fmt(r.SS)}</td>
              <td className="px-3 py-1.5 border border-[#E8E6E1] text-xs text-center font-mono">{fmt(r.MS)}</td>
              <td className="px-3 py-1.5 border border-[#E8E6E1] text-xs text-center font-mono">{fmt(r.F)}</td>
              <td className="px-3 py-1.5 border border-[#E8E6E1] text-xs text-center font-mono">{fmt(r.p_value, 6)}</td>
              <td className="px-3 py-1.5 border border-[#E8E6E1] text-xs text-center">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${r.Sig === '**' ? 'bg-emerald-500 text-white' : r.Sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {r.Sig || '-'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* Color-coded matrix cell */
function matrixCellBg(v, gm) {
  if (v === null || v === undefined || typeof v !== 'number' || !gm) return 'bg-white';
  const diff = (v - gm) / gm;
  if (diff > 0.1) return 'bg-emerald-200';
  if (diff > 0.03) return 'bg-emerald-100';
  if (diff < -0.1) return 'bg-red-200';
  if (diff < -0.03) return 'bg-red-100';
  return 'bg-white';
}

function GxEMatrix({ matrix, grandMean, title = 'Genotype × Environment Means' }) {
  if (!matrix || Object.keys(matrix).length === 0) return null;
  const genos = Object.keys(matrix);
  const envs = Object.keys(matrix[genos[0]]).filter(k => k !== 'Grand_Mean');
  return (
    <div className="overflow-x-auto" data-testid="gxe-matrix">
      <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">{title}</h4>
      <table className="text-xs border-collapse">
        <thead>
          <tr className="bg-[#2D6A4F] text-white">
            <th className="px-3 py-2 border border-[#1B4332] text-left">Genotype</th>
            {envs.map(e => <th key={e} className="px-3 py-2 border border-[#1B4332]">{e}</th>)}
            <th className="px-3 py-2 border border-[#1B4332] bg-[#064E3B]">Mean</th>
          </tr>
        </thead>
        <tbody>
          {genos.map((g, ri) => (
            <tr key={g}>
              <td className="px-3 py-1.5 border border-[#E8E6E1] font-semibold">{g}</td>
              {envs.map(e => {
                const v = matrix[g][e];
                return (
                  <td key={e} className={`px-3 py-1.5 border border-[#E8E6E1] text-center font-mono ${matrixCellBg(v, grandMean)}`}>
                    {fmt(v, 2)}
                  </td>
                );
              })}
              <td className="px-3 py-1.5 border border-[#E8E6E1] text-center font-mono bg-[#ECFDF5] font-semibold">{fmt(matrix[g].Grand_Mean, 2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-gray-500 mt-1">Green = above grand mean · Red = below · Shade intensity = magnitude</p>
    </div>
  );
}

/* ============================================================
   POOLED ANOVA VIEW
============================================================ */
export function PooledAnovaView({ data, analysisId }) {
  const [trait, setTrait] = useState(data?.traits_analyzed?.[0]);
  if (!data) return null;
  const traits = data.traits_analyzed || [];
  const t = data.per_trait?.[trait];

  return (
    <div className="space-y-5" data-testid="pooled-anova-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-xl p-4 border border-emerald-200">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Top summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['Structure', data.structure?.replace(/_/g, ' ')],
          ['Genotypes', data.n_genotypes],
          ['Environments', data.n_environments],
          ['Reps', data.n_reps],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-gray-200">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{k}</div>
            <div className="font-mono text-base font-bold text-[#064E3B]">{v}</div>
          </div>
        ))}
      </div>

      {traits.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {traits.map(tr => (
            <button key={tr} onClick={() => setTrait(tr)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition"
              style={{
                background: trait === tr ? '#059669' : 'white',
                color: trait === tr ? 'white' : '#374151',
                border: '1px solid #D1D5DB',
              }}
              data-testid={`trait-tab-${tr}`}>
              {tr}
            </button>
          ))}
        </div>
      )}

      {t && (
        <>
          {/* Card 1: Homogeneity */}
          <div className="rounded-xl p-4" style={{
            background: t.bartlett?.homogeneous ? '#ECFDF5' : '#FEF3C7',
            border: `1px solid ${t.bartlett?.homogeneous ? '#86EFAC' : '#FCD34D'}`
          }} data-testid="bartlett-card">
            <h4 className="font-semibold text-sm uppercase tracking-wider mb-2" style={{ color: t.bartlett?.homogeneous ? '#064E3B' : '#92400E' }}>
              Bartlett Homogeneity Test
            </h4>
            <div className="flex flex-wrap gap-4 text-sm">
              <span><b>χ²</b> = <span className="font-mono">{fmt(t.bartlett?.chi2)}</span></span>
              <span><b>df</b> = <span className="font-mono">{t.bartlett?.df}</span></span>
              <span><b>p</b> = <span className="font-mono">{fmt(t.bartlett?.p_value, 6)}</span></span>
              <span className={`px-2 py-0.5 rounded font-bold text-xs ${t.bartlett?.homogeneous ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}`}>
                {t.bartlett?.homogeneous ? 'Variances Homogeneous' : 'Variances Heterogeneous'}
              </span>
            </div>
            <p className="text-xs mt-2 leading-relaxed">{t.bartlett?.note}</p>
          </div>

          {/* Individual ANOVAs */}
          {t.individual_anovas && (
            <details className="rounded-xl border border-gray-200 bg-white">
              <summary className="cursor-pointer px-4 py-3 font-semibold text-sm uppercase tracking-wider text-[#2C2A28] flex items-center gap-2">
                <ChevronRight size={14} /> Individual Environment ANOVAs ({t.individual_anovas.length})
              </summary>
              <div className="px-4 pb-4 overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead><tr className="bg-[#F5F4F2]">
                    {['Environment', 'df(Trt)', 'df(Err)', 'MS(Trt)', 'MS(Err)', 'F', 'p', 'Sig', 'CV%'].map(h => (
                      <th key={h} className="border border-gray-200 px-2 py-1.5 text-center">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {t.individual_anovas.map((r, i) => (
                      <tr key={i}>
                        <td className="border border-gray-200 px-2 py-1 font-semibold">{r.Environment}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{r.df_Treatment}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{r.df_Error}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.MS_Treatment)}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.MS_Error)}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.F)}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.p_value, 6)}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center">{r.Sig}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.CV_percent, 2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Pooled ANOVA */}
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">Pooled ANOVA</h4>
            <AnovaTable rows={t.pooled_anova} testIdSuffix="pooled" />
          </div>

          {/* Genotype × Env matrix */}
          <GxEMatrix matrix={t.genotype_env_matrix} grandMean={t.grand_mean} />

          {/* Grand means ranked with CD */}
          {t.grand_means_ranked && (
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">
                Genotype Grand Means (CD @ 5% = {fmt(t.cd_5pct_genotype_grand_means, 3)}, CV = {fmt(t.cv_percent, 2)}%)
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead><tr className="bg-[#2D6A4F] text-white">
                    {['Rank', 'Genotype', 'Mean', 'Group'].map(h => (
                      <th key={h} className="px-3 py-2 border border-[#1B4332]">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {t.grand_means_ranked.map((r, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
                        <td className="px-3 py-1.5 border border-gray-200 text-center font-mono">{r.Rank}</td>
                        <td className="px-3 py-1.5 border border-gray-200 font-semibold">{r.Genotype}</td>
                        <td className="px-3 py-1.5 border border-gray-200 text-center font-mono">{fmt(r.Mean)}</td>
                        <td className="px-3 py-1.5 border border-gray-200 text-center font-bold text-emerald-700">{r.Group}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Variance components */}
          {t.variance_components && (
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">Variance Components & Genetic Parameters</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {Object.entries(t.variance_components).map(([k, v]) => (
                  <div key={k} className="rounded-lg p-2.5 bg-white border border-gray-200">
                    <div className="text-[10px] text-gray-500 uppercase leading-tight">{k.replace(/_/g, ' ')}</div>
                    <div className="font-mono text-sm font-bold text-[#064E3B] mt-0.5">{fmt(v, 4)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* GxE significance & transfer buttons */}
          {t.gxe_significant && (
            <TransferButtons analysisId={analysisId}
              label="G×E is significant — run stability analysis"
              targetKeys={[
                { key: 'ammi', label: 'Run AMMI', section: 'stability' },
                { key: 'gge', label: 'Run GGE Biplot', section: 'stability' },
                { key: 'comp-stability', label: 'Run Comprehensive Stability', section: 'stability' },
                { key: 'eberhart', label: 'Run Eberhart–Russell', section: 'stability' },
              ]} />
          )}
        </>
      )}
    </div>
  );
}

/* ============================================================
   MANOVA ENHANCED VIEW
============================================================ */
export function ManovaEnhancedView({ data, analysisId }) {
  if (!data) return null;
  return (
    <div className="space-y-5" data-testid="manova-enhanced-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-xl p-4 border border-emerald-200">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ['Treatments', data.n_treatments],
          ['Traits', data.n_traits],
          ['Observations', data.n_observations],
          ['Recommended', data.assumptions?.recommendation],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-gray-200">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{k}</div>
            <div className="font-mono text-sm font-bold text-[#064E3B]">{v}</div>
          </div>
        ))}
      </div>

      {/* Assumptions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl p-4 bg-white border border-gray-200" data-testid="mardia-card">
          <h5 className="font-semibold text-xs uppercase tracking-wider text-[#2C2A28] mb-2">Mardia Multivariate Normality</h5>
          <div className="text-xs space-y-1">
            <div>Skewness: <span className="font-mono">{fmt(data.assumptions?.mardia?.skewness_stat)}</span> (p = {fmt(data.assumptions?.mardia?.skewness_p, 6)}) <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${data.assumptions?.mardia?.normal_skew ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>{data.assumptions?.mardia?.normal_skew ? '✓ Normal' : '⚠ Non-normal'}</span></div>
            <div>Kurtosis Z: <span className="font-mono">{fmt(data.assumptions?.mardia?.kurtosis_z)}</span> (p = {fmt(data.assumptions?.mardia?.kurtosis_p, 6)}) <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${data.assumptions?.mardia?.normal_kurt ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>{data.assumptions?.mardia?.normal_kurt ? '✓ Normal' : '⚠ Non-normal'}</span></div>
          </div>
        </div>
        <div className="rounded-xl p-4 bg-white border border-gray-200" data-testid="boxm-card">
          <h5 className="font-semibold text-xs uppercase tracking-wider text-[#2C2A28] mb-2">Box's M (Covariance Homogeneity)</h5>
          <div className="text-xs space-y-1">
            <div>M = <span className="font-mono">{fmt(data.assumptions?.box_m?.M_statistic)}</span></div>
            <div>χ² = <span className="font-mono">{fmt(data.assumptions?.box_m?.chi2)}</span>, df = {data.assumptions?.box_m?.df}</div>
            <div>p = <span className="font-mono">{fmt(data.assumptions?.box_m?.p_value, 6)}</span>
              <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${data.assumptions?.box_m?.homogeneous ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>
                {data.assumptions?.box_m?.homogeneous ? '✓ Homogeneous' : '⚠ Heterogeneous'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 4 MANOVA statistics */}
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">Multivariate Test Statistics</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead><tr className="bg-[#2D6A4F] text-white">
              {['Test', 'Statistic', 'F', 'df1', 'df2', 'p', 'Sig', ''].map(h => (
                <th key={h} className="px-3 py-2 border border-[#1B4332] text-xs">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(data.multivariate_tests || []).map((r, i) => (
                <tr key={i} className={r.Recommended ? 'bg-emerald-50' : (i % 2 === 0 ? 'bg-white' : 'bg-gray-50')}>
                  <td className="px-3 py-1.5 border border-gray-200 font-semibold">{r.Test}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center font-mono">{fmt(r.Statistic)}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center font-mono">{fmt(r.F)}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center font-mono">{r.df1}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center font-mono">{r.df2}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center font-mono">{fmt(r.p_value, 6)}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${r.Sig === '**' ? 'bg-emerald-500 text-white' : r.Sig === '*' ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {r.Sig}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center">
                    {r.Recommended && <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-600 text-white">★ Recommended</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Overall conclusion */}
      <div className={`rounded-xl p-4 text-sm ${data.overall_significant ? 'bg-emerald-50 border border-emerald-300' : 'bg-gray-50 border border-gray-300'}`}>
        <b>Overall conclusion:</b> {data.conclusion}
      </div>

      {/* Univariate follow-up */}
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">
          Univariate Follow-up (Bonferroni α = {fmt(data.bonferroni_alpha, 6)})
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead><tr className="bg-[#2D6A4F] text-white">
              {['Trait', 'F', 'p', 'Sig (0.05)', 'Sig (Bonferroni)'].map(h => (
                <th key={h} className="px-3 py-2 border border-[#1B4332] text-xs">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(data.univariate_followup || []).map((r, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F0FAF0]'}>
                  <td className="px-3 py-1.5 border border-gray-200 font-semibold">{r.Trait}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center font-mono">{fmt(r.F)}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center font-mono">{fmt(r.p_value, 6)}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center">{r['Sig_0.05']}</td>
                  <td className="px-3 py-1.5 border border-gray-200 text-center font-bold">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${r.Sig_Bonferroni === '**' ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {r.Sig_Bonferroni}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Canonical discriminant scatter */}
      {data.canonical_scores && data.canonical_scores.length > 0 && (
        <CanonicalPlot scores={data.canonical_scores} varExpl={data.canonical_variance_explained_percent || [0, 0]} />
      )}

      {/* Most different pair */}
      {data.most_different_pair?.pair && (
        <div className="rounded-xl p-4 bg-amber-50 border border-amber-300">
          <span className="text-xs uppercase tracking-wider text-amber-800 font-bold">Most Different Pair</span>
          <p className="mt-1 text-sm"><b>{data.most_different_pair.pair}</b> · Mahalanobis D² = <span className="font-mono">{fmt(data.most_different_pair.distance)}</span></p>
        </div>
      )}

      {/* Mahalanobis distances */}
      {data.mahalanobis_distances?.length > 0 && (
        <details className="rounded-xl border border-gray-200 bg-white">
          <summary className="cursor-pointer px-4 py-3 font-semibold text-sm uppercase tracking-wider text-[#2C2A28]">
            All Pairwise Mahalanobis Distances ({data.mahalanobis_distances.length})
          </summary>
          <div className="px-4 pb-4 overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead><tr className="bg-[#F5F4F2]">
                {['A', 'B', 'Mahalanobis D²'].map(h => <th key={h} className="border border-gray-200 px-2 py-1.5">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.mahalanobis_distances.map((r, i) => (
                  <tr key={i}>
                    <td className="border border-gray-200 px-2 py-1">{r.A}</td>
                    <td className="border border-gray-200 px-2 py-1">{r.B}</td>
                    <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.Mahalanobis_D2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {data.overall_significant && (
        <TransferButtons analysisId={analysisId}
          label="Follow-up recommendations"
          targetKeys={[
            { key: 'discriminant', label: 'Discriminant Analysis', section: 'multivariate' },
            { key: 'pca-bio', label: 'PCA', section: 'multivariate' },
          ]} />
      )}
    </div>
  );
}

/* Minimal SVG scatter for canonical scores */
function CanonicalPlot({ scores, varExpl }) {
  if (!scores || scores.length === 0) return null;
  const xs = scores.map(s => s.CV1).filter(v => typeof v === 'number');
  const ys = scores.map(s => s.CV2).filter(v => typeof v === 'number');
  const xmin = Math.min(...xs), xmax = Math.max(...xs);
  const ymin = Math.min(...ys), ymax = Math.max(...ys);
  const pad = 30;
  const W = 560, H = 380;
  const sx = v => pad + (v - xmin) / ((xmax - xmin) || 1) * (W - 2 * pad);
  const sy = v => H - pad - (v - ymin) / ((ymax - ymin) || 1) * (H - 2 * pad);

  // Palette per treatment
  const palette = ['#059669', '#DC2626', '#2563EB', '#D97706', '#7C3AED', '#DB2777', '#0D9488', '#B45309', '#4F46E5', '#16A34A'];
  const trMap = {};
  const treatments = Array.from(new Set(scores.map(s => s.Treatment)));
  treatments.forEach((t, i) => { trMap[t] = palette[i % palette.length]; });

  return (
    <div className="rounded-xl p-4 bg-white border border-gray-200" data-testid="canonical-plot">
      <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">
        Canonical Discriminant Plot (CV1: {varExpl[0]}% · CV2: {varExpl[1]}%)
      </h4>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} className="bg-gray-50 rounded">
        {/* axes */}
        <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="#374151" strokeWidth="1" />
        <line x1={pad} y1={pad} x2={pad} y2={H - pad} stroke="#374151" strokeWidth="1" />
        <text x={W / 2} y={H - 5} textAnchor="middle" fontSize="11" fill="#374151">CV1 ({varExpl[0]}%)</text>
        <text x={12} y={H / 2} transform={`rotate(-90 12 ${H / 2})`} textAnchor="middle" fontSize="11" fill="#374151">CV2 ({varExpl[1]}%)</text>
        {/* points */}
        {scores.map((s, i) => (
          <circle key={i} cx={sx(s.CV1)} cy={sy(s.CV2)} r="5" fill={trMap[s.Treatment]} fillOpacity="0.7" stroke="#fff" strokeWidth="1" />
        ))}
      </svg>
      {/* legend */}
      <div className="flex flex-wrap gap-3 mt-2 text-xs">
        {treatments.map(t => (
          <div key={t} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: trMap[t] }} />
            <span>{t}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   FULL MET VIEW
============================================================ */
export function FullMETView({ data, analysisId }) {
  const [trait, setTrait] = useState(data?.data_summary?.traits?.[0]);
  if (!data) return null;
  const traits = data.data_summary?.traits || [];
  const t = data.per_trait?.[trait];

  return (
    <div className="space-y-5" data-testid="full-met-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-xl p-4 border border-emerald-200">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Data summary card */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ['Genotypes', data.data_summary?.n_genotypes],
          ['Locations', data.data_summary?.n_locations],
          ['Years', data.data_summary?.n_years],
          ['Environments', data.data_summary?.n_environments],
          ['Reps', data.data_summary?.n_reps],
          ['Traits', data.data_summary?.n_traits],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg p-2.5 bg-white border border-gray-200">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{k}</div>
            <div className="font-mono text-base font-bold text-[#064E3B]">{v}</div>
          </div>
        ))}
      </div>

      <div className={`rounded-xl p-3 text-sm ${data.data_summary?.balance === 'Balanced' ? 'bg-emerald-50 border border-emerald-300' : 'bg-amber-50 border border-amber-300'}`}>
        <b>{data.data_summary?.balance}</b>
      </div>

      {traits.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {traits.map(tr => (
            <button key={tr} onClick={() => setTrait(tr)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition"
              style={{
                background: trait === tr ? '#059669' : 'white',
                color: trait === tr ? 'white' : '#374151',
                border: '1px solid #D1D5DB',
              }}>
              {tr}
            </button>
          ))}
        </div>
      )}

      {t && (
        <>
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">Three-way ANOVA</h4>
            <AnovaTable rows={t.anova_table} testIdSuffix="met-full" />
          </div>

          {/* Variance components */}
          {t.variance_components && (
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">Variance Components & Heritability</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {Object.entries(t.variance_components).map(([k, v]) => (
                  <div key={k} className="rounded-lg p-2.5 bg-white border border-gray-200">
                    <div className="text-[10px] text-gray-500 uppercase leading-tight">{k.replace(/_/g, ' ')}</div>
                    <div className="font-mono text-sm font-bold text-[#064E3B] mt-0.5">{fmt(v, 4)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* GxE matrix */}
          <GxEMatrix matrix={t.genotype_env_matrix} grandMean={t.grand_mean} title="Performance Matrix (Genotype × Environment)" />

          {/* Rank correlations */}
          {t.rank_correlations?.length > 0 && (
            <details className="rounded-xl border border-gray-200 bg-white">
              <summary className="cursor-pointer px-4 py-3 font-semibold text-sm uppercase tracking-wider text-[#2C2A28]">
                Spearman Rank Correlations Between Environments ({t.rank_correlations.length})
              </summary>
              <div className="px-4 pb-4 overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead><tr className="bg-[#F5F4F2]">
                    {['Env A', 'Env B', 'ρ (Spearman)', 'p'].map(h => <th key={h} className="border border-gray-200 px-2 py-1.5">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {t.rank_correlations.map((r, i) => (
                      <tr key={i}>
                        <td className="border border-gray-200 px-2 py-1">{r.Env_A}</td>
                        <td className="border border-gray-200 px-2 py-1">{r.Env_B}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.Spearman_rho)}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.p_value, 6)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Best / stable / combination */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-xl p-4 bg-emerald-50 border border-emerald-300">
              <div className="text-xs uppercase tracking-wider text-emerald-800 font-bold">Best Overall</div>
              <div className="text-lg font-bold mt-1">{t.best_overall_genotype}</div>
              <p className="text-xs mt-0.5 text-gray-600">Highest grand mean</p>
            </div>
            <div className="rounded-xl p-4 bg-blue-50 border border-blue-300">
              <div className="text-xs uppercase tracking-wider text-blue-800 font-bold">Most Stable</div>
              <div className="text-lg font-bold mt-1">{t.most_stable_genotype}</div>
              <p className="text-xs mt-0.5 text-gray-600">Lowest across-env variance</p>
            </div>
            <div className="rounded-xl p-4 bg-purple-50 border border-purple-300">
              <div className="text-xs uppercase tracking-wider text-purple-800 font-bold">Best Combination (HMRPGV)</div>
              <div className="text-lg font-bold mt-1">{t.best_combination_genotype}</div>
              <p className="text-xs mt-0.5 text-gray-600">High yield AND stable</p>
            </div>
          </div>

          {/* Best per env */}
          {t.best_per_environment?.length > 0 && (
            <details className="rounded-xl border border-gray-200 bg-white">
              <summary className="cursor-pointer px-4 py-3 font-semibold text-sm uppercase tracking-wider text-[#2C2A28]">
                Best Genotype per Environment
              </summary>
              <div className="px-4 pb-4 overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead><tr className="bg-[#F5F4F2]">
                    {['Environment', 'Best Genotype', 'Mean'].map(h => <th key={h} className="border border-gray-200 px-2 py-1.5">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {t.best_per_environment.map((r, i) => (
                      <tr key={i}>
                        <td className="border border-gray-200 px-2 py-1">{r.Environment}</td>
                        <td className="border border-gray-200 px-2 py-1 font-semibold">{r.Best_Genotype}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.Mean)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Mega-environment Delineation */}
          {t.mega_environments && t.mega_environments.n_clusters > 0 && (
            <div className="space-y-3" data-testid="mega-env-panel">
              <div className="rounded-xl p-4 bg-emerald-50 border border-emerald-300">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs uppercase tracking-wider font-bold text-emerald-900">Mega-environment Delineation</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-700 text-white font-bold">
                    {t.mega_environments.n_clusters} cluster{t.mega_environments.n_clusters > 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-sm text-emerald-900 leading-relaxed">{t.mega_environments.recommendation}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                  {t.mega_environments.clusters.map((c, i) => {
                    const palette = ['#059669', '#DC2626', '#2563EB', '#D97706', '#7C3AED', '#DB2777'];
                    const color = palette[i % palette.length];
                    return (
                      <div key={c.label} className="rounded-lg p-3 bg-white border-l-4" style={{ borderLeftColor: color }} data-testid={`me-cluster-${c.label}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold" style={{ color }}>{c.label}</span>
                          <span className="text-[10px] text-gray-500">{c.environments.length} env{c.environments.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="text-sm mb-2"><b>Best:</b> {c.best_genotype} <span className="text-gray-500 font-mono text-[11px]">({fmt(c.best_genotype_mean, 2)})</span></div>
                        <div className="flex flex-wrap gap-1">
                          {c.environments.map(e => (
                            <span key={e} className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-gray-100 border border-gray-300">{e}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* India map */}
              <IndiaMegaEnvMap clusters={t.mega_environments.clusters} />
            </div>
          )}

          {/* Breeding Zone Recommendation */}
          {t.breeding_zone_recommendation && (
            <div className="rounded-xl p-4 border-2"
              style={{
                background: t.breeding_zone_recommendation.recommendation === 'broad' ? '#F0FDF4' :
                            t.breeding_zone_recommendation.recommendation === 'zone-specific' ? '#FEF2F2' : '#FFFBEB',
                borderColor: t.breeding_zone_recommendation.recommendation === 'broad' ? '#86EFAC' :
                             t.breeding_zone_recommendation.recommendation === 'zone-specific' ? '#FCA5A5' : '#FCD34D',
              }}
              data-testid="breeding-zone-rec"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider font-bold" style={{ color: '#064E3B' }}>
                  Breeding Zone Recommendation
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white font-bold border border-gray-300">
                  Confidence: {t.breeding_zone_recommendation.confidence}
                </span>
              </div>
              <div className="text-base font-bold mb-1" style={{ color: '#064E3B' }}>
                {t.breeding_zone_recommendation.label}
              </div>
              <p className="text-sm leading-relaxed">{t.breeding_zone_recommendation.description}</p>
              <div className="flex gap-4 mt-2 text-xs text-gray-600">
                <span>GxE/VG ratio: <b>{fmt(t.breeding_zone_recommendation.gxe_vg_ratio, 3)}</b></span>
                <span>GxE as % of VP: <b>{fmt(t.breeding_zone_recommendation.gxe_pct_of_vp, 2)}%</b></span>
              </div>
            </div>
          )}

          {t.gxe_significant && (
            <TransferButtons analysisId={analysisId}
              label="G×E is significant — run stability analysis"
              targetKeys={[
                { key: 'ammi', label: 'Run AMMI', section: 'stability' },
                { key: 'gge', label: 'Run GGE Biplot', section: 'stability' },
                { key: 'comp-stability', label: 'Run Comprehensive Stability', section: 'stability' },
              ]} />
          )}
        </>
      )}
    </div>
  );
}

/* ============================================================
   GENOTYPE × YEAR VIEW
============================================================ */
export function GenotypeYearView({ data, analysisId }) {
  const [trait, setTrait] = useState(data?.traits_analyzed?.[0]);
  if (!data) return null;
  const traits = data.traits_analyzed || [];
  const t = data.per_trait?.[trait];

  return (
    <div className="space-y-5" data-testid="genotype-year-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-xl p-4 border border-emerald-200">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {[
          ['Genotypes', data.n_genotypes],
          ['Years', data.n_years],
          ['Reps', data.n_reps],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg p-3 bg-white border border-gray-200">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{k}</div>
            <div className="font-mono text-base font-bold text-[#064E3B]">{v}</div>
          </div>
        ))}
      </div>

      {traits.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {traits.map(tr => (
            <button key={tr} onClick={() => setTrait(tr)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition"
              style={{
                background: trait === tr ? '#059669' : 'white',
                color: trait === tr ? 'white' : '#374151',
                border: '1px solid #D1D5DB',
              }}>
              {tr}
            </button>
          ))}
        </div>
      )}

      {t && (
        <>
          <div>
            <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">Pooled G×Y ANOVA</h4>
            <AnovaTable rows={t.pooled_anova} testIdSuffix="gxy-pooled" />
          </div>

          {/* Year-wise */}
          <details className="rounded-xl border border-gray-200 bg-white">
            <summary className="cursor-pointer px-4 py-3 font-semibold text-sm uppercase tracking-wider text-[#2C2A28]">
              Year-wise ANOVAs
            </summary>
            <div className="px-4 pb-4 overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead><tr className="bg-[#F5F4F2]">
                  {['Year', 'df(G)', 'df(Err)', 'MS(G)', 'MS(Err)', 'F', 'p', 'Sig', 'CV%'].map(h => (
                    <th key={h} className="border border-gray-200 px-2 py-1.5">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(t.yearly_anovas || []).map((r, i) => (
                    <tr key={i}>
                      <td className="border border-gray-200 px-2 py-1 font-semibold">{r.Year}</td>
                      <td className="border border-gray-200 px-2 py-1 text-center font-mono">{r.df_Genotype}</td>
                      <td className="border border-gray-200 px-2 py-1 text-center font-mono">{r.df_Error}</td>
                      <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.MS_Genotype)}</td>
                      <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.MS_Error)}</td>
                      <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.F)}</td>
                      <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.p_value, 6)}</td>
                      <td className="border border-gray-200 px-2 py-1 text-center">{r.Sig}</td>
                      <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.CV_percent, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          {/* Temporal trends chart + table */}
          {t.temporal_trends?.length > 0 && <TemporalTrendChart trends={t.temporal_trends} />}

          {/* Stability ranking */}
          {t.most_stable_genotypes?.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">Stability Ranking (lowest G×Y variance first)</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead><tr className="bg-[#2D6A4F] text-white">
                    {['Rank', 'Genotype', 'G×Y Variance'].map(h => <th key={h} className="px-2 py-1.5 border border-[#1B4332]">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {t.most_stable_genotypes.map((r, i) => (
                      <tr key={i} className={i === 0 ? 'bg-emerald-50' : ''}>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{i + 1}</td>
                        <td className="border border-gray-200 px-2 py-1 font-semibold">{r.Genotype}</td>
                        <td className="border border-gray-200 px-2 py-1 text-center font-mono">{fmt(r.GxY_Variance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Realized genetic gain */}
          {t.realized_genetic_gain && (
            <div className="rounded-xl p-4 bg-indigo-50 border border-indigo-300" data-testid="realized-gain">
              <h4 className="font-semibold text-sm uppercase tracking-wider text-indigo-900 mb-2">Realized Genetic Gain</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {[
                  ['Gain / year', fmt(t.realized_genetic_gain.realized_gain_per_year)],
                  ['% / year', fmt(t.realized_genetic_gain.realized_gain_percent_per_year, 2)],
                  ['Slope p', fmt(t.realized_genetic_gain.slope_p_value, 6)],
                  ['Heritability', fmt(t.realized_genetic_gain.heritability)],
                  ['Expected gain/cycle', fmt(t.realized_genetic_gain.expected_gain_per_selection_cycle)],
                ].map(([k, v]) => (
                  <div key={k} className="bg-white rounded p-2 border border-indigo-200">
                    <div className="text-[10px] text-gray-500 uppercase">{k}</div>
                    <div className="font-mono font-bold text-indigo-900">{v}</div>
                  </div>
                ))}
              </div>
              <p className="text-sm mt-3">{t.realized_genetic_gain.interpretation}</p>
            </div>
          )}

          {/* Year Effect Decomposition */}
          {t.year_effect_decomposition && (
            <div className="rounded-xl p-4 border-2"
              style={{
                background: t.year_effect_decomposition.pattern?.includes('Climate') ? '#FEF2F2' :
                            t.year_effect_decomposition.pattern?.includes('Weather') ? '#EFF6FF' : '#FFFBEB',
                borderColor: t.year_effect_decomposition.pattern?.includes('Climate') ? '#FCA5A5' :
                             t.year_effect_decomposition.pattern?.includes('Weather') ? '#93C5FD' : '#FCD34D',
              }}
              data-testid="year-decomposition"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider font-bold text-gray-900">
                  Year Effect Decomposition
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white font-bold border border-gray-300">
                  Pattern: {t.year_effect_decomposition.pattern}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-3">
                <div className="bg-white rounded p-2 border border-gray-200">
                  <div className="text-[10px] text-gray-500 uppercase">Linear Slope/Year</div>
                  <div className="font-mono font-bold">{fmt(t.year_effect_decomposition.linear_slope_per_year)}</div>
                </div>
                <div className="bg-white rounded p-2 border border-gray-200">
                  <div className="text-[10px] text-gray-500 uppercase">R²</div>
                  <div className="font-mono font-bold">{fmt(t.year_effect_decomposition.linear_R2)}</div>
                </div>
                <div className="bg-white rounded p-2 border border-gray-200">
                  <div className="text-[10px] text-gray-500 uppercase">% Linear Trend</div>
                  <div className="font-mono font-bold text-red-700">{fmt(t.year_effect_decomposition.pct_variance_linear_trend, 1)}%</div>
                </div>
                <div className="bg-white rounded p-2 border border-gray-200">
                  <div className="text-[10px] text-gray-500 uppercase">% Weather Flux</div>
                  <div className="font-mono font-bold text-blue-700">{fmt(t.year_effect_decomposition.pct_variance_weather_fluctuation, 1)}%</div>
                </div>
              </div>
              {/* Stacked bar showing the split */}
              <div className="w-full h-6 rounded overflow-hidden bg-gray-200 flex mb-2">
                <div
                  className="h-full flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ width: `${t.year_effect_decomposition.pct_variance_linear_trend}%`, background: '#DC2626' }}
                  title="Linear climate trend"
                >
                  {t.year_effect_decomposition.pct_variance_linear_trend > 15 ? 'Linear trend' : ''}
                </div>
                <div
                  className="h-full flex items-center justify-center text-[10px] font-bold text-white"
                  style={{ width: `${t.year_effect_decomposition.pct_variance_weather_fluctuation}%`, background: '#2563EB' }}
                  title="Weather fluctuation"
                >
                  {t.year_effect_decomposition.pct_variance_weather_fluctuation > 15 ? 'Weather flux' : ''}
                </div>
              </div>
              <p className="text-sm mt-2 leading-relaxed">{t.year_effect_decomposition.insight}</p>
            </div>
          )}

          {t.gxy_significant && (
            <TransferButtons analysisId={analysisId}
              label="G×Y is significant — analyze year stability"
              targetKeys={[
                { key: 'eberhart', label: 'Run Eberhart–Russell', section: 'stability' },
                { key: 'comp-stability', label: 'Run Comprehensive Stability', section: 'stability' },
              ]} />
          )}
        </>
      )}
    </div>
  );
}

/* Tiny SVG line chart for temporal trends */
function TemporalTrendChart({ trends }) {
  if (!trends || trends.length === 0) return null;
  const allPoints = trends.flatMap(t => t.points || []);
  if (!allPoints.length) return null;
  const xs = allPoints.map(p => p.x);
  const ys = allPoints.map(p => p.y);
  const xmin = Math.min(...xs), xmax = Math.max(...xs);
  const ymin = Math.min(...ys), ymax = Math.max(...ys);
  const pad = 40;
  const W = 700, H = 380;
  const sx = v => pad + (v - xmin) / ((xmax - xmin) || 1) * (W - 2 * pad);
  const sy = v => H - pad - (v - ymin) / ((ymax - ymin) || 1) * (H - 2 * pad);

  const palette = ['#059669', '#DC2626', '#2563EB', '#D97706', '#7C3AED', '#DB2777', '#0D9488', '#B45309', '#4F46E5', '#16A34A', '#EA580C', '#9333EA'];

  return (
    <div className="rounded-xl p-4 bg-white border border-gray-200" data-testid="temporal-trend-chart">
      <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2C2A28] mb-2">Temporal Performance Trends (Per Genotype)</h4>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} className="bg-gray-50 rounded">
        <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} stroke="#374151" strokeWidth="1" />
        <line x1={pad} y1={pad} x2={pad} y2={H - pad} stroke="#374151" strokeWidth="1" />
        <text x={W / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="#374151">Year</text>
        <text x={14} y={H / 2} transform={`rotate(-90 14 ${H / 2})`} textAnchor="middle" fontSize="11" fill="#374151">Mean</text>
        {trends.map((t, i) => {
          const pts = (t.points || []).map(p => `${sx(p.x)},${sy(p.y)}`).join(' ');
          const color = palette[i % palette.length];
          return (
            <g key={t.Genotype}>
              <polyline points={pts} fill="none" stroke={color} strokeWidth="2" opacity="0.85" />
              {(t.points || []).map((p, j) => (
                <circle key={j} cx={sx(p.x)} cy={sy(p.y)} r="3" fill={color} />
              ))}
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-2 mt-2 text-[11px]">
        {trends.map((t, i) => (
          <div key={t.Genotype} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: palette[i % palette.length] }} />
            <span>{t.Genotype} <b style={{ color: t.Trend === 'Improving' ? '#059669' : t.Trend === 'Declining' ? '#DC2626' : '#6B7280' }}>({t.Trend})</b></span>
          </div>
        ))}
      </div>
    </div>
  );
}
